/**
 * The platform's own achievements — the ones the system awards, as opposed to a collection's.
 *
 * There was no screen for these at all. They were created by calling the Admin API by hand or by
 * running SQL against production, which is also how production came to have none: the seeder that
 * defines them only runs against a database that does not yet exist.
 */

import { useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EmojiEvents as TrophyIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  VisibilityOff as HiddenIcon,
} from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { SearchField } from '@components/common/SearchField'
import type { RowAction } from '@components/common/RowActions'
import {
  CRITERIA_LABELS,
  toThresholdInput,
  type SystemAchievementSummaryDto,
} from '@api/types/systemAchievement'
import {
  useDeleteSystemAchievement,
  useRecalculateCriteria,
  useSystemAchievements,
} from '../../hooks/useSystemAchievements'
import { SystemAchievementFormDialog } from './SystemAchievementFormDialog'
import { AchievementRedateDialog } from './AchievementRedateCard'

export function SystemAchievementListTable() {
  const [formOpen, setFormOpen] = useState(false)
  const [selected, setSelected] = useState<SystemAchievementSummaryDto | null>(null)
  const [toDelete, setToDelete] = useState<SystemAchievementSummaryDto | null>(null)
  const [toRecalc, setToRecalc] = useState<SystemAchievementSummaryDto | null>(null)
  const [redateOpen, setRedateOpen] = useState(false)
  const [search, setSearch] = useState('')

  // Paging state lives here; DataTable never owns it.
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data: achievements, isLoading, error, refetch } = useSystemAchievements()
  const deleteMutation = useDeleteSystemAchievement()
  const recalcMutation = useRecalculateCriteria()

  const handleDeleteConfirm = async () => {
    if (!toDelete) return
    try {
      await deleteMutation.mutateAsync(toDelete.id)
      setToDelete(null)
    } catch {
      // Surfaced by the mutation's error below.
    }
  }

  // The whole list comes down in one request, so search filters it here.
  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return achievements ?? []
    return (achievements ?? []).filter((a) =>
      [a.name, a.shortDescription, CRITERIA_LABELS[a.criteriaType]?.label]
        .some((v) => v?.toLowerCase().includes(q))
    )
  }, [achievements, search])

  const getRowActions = (a: SystemAchievementSummaryDto): RowAction[] => [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIcon fontSize="small" />,
      onClick: () => {
        setSelected(a)
        setFormOpen(true)
      },
    },
    {
      key: 'recalc',
      label: 'Recalculate progress',
      hint: "Recalculate everyone's progress for this criterion",
      icon: <CalculateIcon fontSize="small" />,
      disabled: recalcMutation.isPending,
      onClick: () => setToRecalc(a),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setToDelete(a),
    },
  ]

  const columns = useMemo<DataTableColumn<SystemAchievementSummaryDto>[]>(
    () => [
      {
        key: 'icon',
        header: 'Icon',
        width: 56,
        render: (a) => (
          <Avatar src={a.iconUrl || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
            <TrophyIcon fontSize="small" />
          </Avatar>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        // The card title on a phone.
        primary: true,
        render: (a) => (
          <Box sx={{ opacity: a.isActive ? 1 : 0.6 }}>
            <Typography variant="body2" fontWeight="medium">
              {a.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {a.shortDescription}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'criterion',
        header: 'Criterion',
        render: (a) => {
          const criterion = CRITERIA_LABELS[a.criteriaType]
          return (
            <Typography variant="body2">
              {criterion?.label ?? `Unknown (${a.criteriaType})`}
            </Typography>
          )
        },
      },
      {
        key: 'threshold',
        header: 'Threshold',
        render: (a) => {
          // The unit, always. A number without its unit is the one thing that makes these
          // unearnable. Distance is stored in metres and shown here in kilometres, matching the
          // box an admin typed it into rather than the column it landed in.
          const criterion = CRITERIA_LABELS[a.criteriaType]
          return (
            <Typography variant="body2">
              {toThresholdInput(a.criteriaType, a.threshold).toLocaleString()}{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                {criterion?.unit ?? ''}
              </Typography>
            </Typography>
          )
        },
      },
      {
        key: 'points',
        header: 'Points',
        hideBelow: 'md',
        render: (a) => <Typography variant="body2">{a.points}</Typography>,
      },
      {
        key: 'status',
        header: 'Status',
        render: (a) => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={a.isActive ? 'Active' : 'Inactive'}
              color={a.isActive ? 'success' : 'default'}
              size="small"
              variant={a.isActive ? 'filled' : 'outlined'}
            />
            {a.isHidden && <Chip icon={<HiddenIcon />} label="Hidden" size="small" variant="outlined" />}
          </Box>
        ),
      },
      {
        key: 'order',
        header: 'Order',
        align: 'right',
        width: 90,
        hideBelow: 'md',
        render: (a) => <Typography variant="body2">{a.displayOrder}</Typography>,
      },
    ],
    []
  )

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            Hidden achievements are listed here; players only see them once earned.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          {/* A one-off repair, so it sits behind a button instead of above the list. */}
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setRedateOpen(true)}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Fix earned dates
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelected(null)
              setFormOpen(true)
            }}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Add Achievement
          </Button>
        </Box>
      </Box>

      {deleteMutation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : 'Failed to delete the achievement'}
        </Alert>
      )}
      {recalcMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => recalcMutation.reset()}>
          Could not recalculate progress.
        </Alert>
      )}
      {recalcMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => recalcMutation.reset()}>
          Progress recalculated. Accounts with no progress row yet pick it up the next time they do
          something that touches this criterion.
        </Alert>
      )}

      <DataTable
        rows={filtered}
        columns={columns}
        getRowKey={(a) => a.id}
        loading={isLoading}
        error={error ? (error instanceof Error ? error.message : 'Failed to load achievements') : null}
        emptyMessage={
          search ? 'No achievements match that search.' : 'No system achievements yet — nothing for anyone to earn.'
        }
        // No `total` => DataTable pages the rows it was given.
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 25, 50]}
        getRowActions={getRowActions}
        onRetry={() => refetch()}
        toolbar={
          <SearchField
            value={search}
            onSearch={(q) => {
              setSearch(q)
              setPage(0)
            }}
            placeholder="Search achievements"
          />
        }
      />

      <SystemAchievementFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        achievement={selected}
      />

      <AchievementRedateDialog open={redateOpen} onClose={() => setRedateOpen(false)} />

      <ConfirmDialog
        open={!!toRecalc}
        title="Recalculate progress?"
        message={
          <>
            Recomputes every player's progress towards all achievements that use the{' '}
            <strong>{toRecalc ? (CRITERIA_LABELS[toRecalc.criteriaType]?.label ?? toRecalc.criteriaType) : ''}</strong>{' '}
            criterion, not just <strong>{toRecalc?.name}</strong>. It runs across every account, so it
            can take a while. Badges already finished keep their dates; new ones may be awarded.
          </>
        }
        confirmLabel="Recalculate"
        loading={recalcMutation.isPending}
        onConfirm={() => {
          if (toRecalc) recalcMutation.mutate(toRecalc.criteriaType, { onSettled: () => setToRecalc(null) })
        }}
        onClose={() => setToRecalc(null)}
      />

      <ConfirmDialog
        open={!!toDelete}
        title="Delete this achievement?"
        message={
          <>
            <strong>{toDelete?.name}</strong> will be removed, along with everybody's progress
            towards it — including from people who had already earned it. Switching it to inactive
            keeps both and stops it accruing.
          </>
        }
        confirmLabel={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onClose={() => setToDelete(null)}
      />
    </Box>
  )
}
